import React from 'react'
import PropTypes from 'prop-types'

import {createSelector} from 'reselect'
import {acttaskByStartDateSelector, statusDateSelector} from '../selectors/dataset'

import Enum from 'swift-enum'

import { VictoryBar,  VictoryChart, Bar} from 'victory'

import Paper from 'material-ui/Paper'
import Typography from 'material-ui/Typography'
import { withStyles } from 'material-ui/styles'
import Grid from 'material-ui/Grid'
import ItemToolbar from './ItemToolbar'
import {chartColors, chartSelection as chartSelection_theme, chartSizes, framework as appTheme } from '../etc/theme'
import TaskList from '../components/TaskList'
import {ClickableBar} from '../etc/victory-utils'
import Divider from 'material-ui/Divider'
import { FormControlLabel, FormGroup } from 'material-ui/Form'
import Switch from 'material-ui/Switch'

import moment from 'moment'

let _categoryShape = PropTypes.shape({
  top: PropTypes.array,
  total: PropTypes.number
})

class Filter extends Enum {
  get title() {
    switch(this)
    {
      case Filter.in_progress: return 'In Progress'
      case Filter.next_seven: return 'Next Seven Days'
      case Filter.next_fourteen: return 'Next Fourteen Days'
      case Filter.none:
      default:
       return ''
    }
  }
}
Filter.case(
  'in_progress',
  'next_seven',
  'next_fourteen',
  'fourteen_plus'
)
Filter.case({none: ''})
Filter.freeze()

const defaultFilter = Filter.none

export {Filter, defaultFilter}

// (dataset, current_date, current_filter) -> [acttask_t]
export let filteredTasksSelector = createSelector(
  acttaskByStartDateSelector,
  (dataset, current_date, current_filter) => (current_filter),
  (by_start, current_filter) => {
    switch(current_filter)
    {
      case Filter.in_progress: return by_start.in_progress
      case Filter.next_seven: return by_start.next_seven
      case Filter.next_fourteen: return by_start.next_fourteen
      case Filter.fourteen_plus: return by_start.fourteen_plus
      case Filter.none:
      default: 
      return [...by_start.in_progress, ...by_start.next_seven, ...by_start.next_fourteen, ...by_start.fourteen_plus]
    }
  }
)

export let taglineSelector = createSelector(
  acttaskByStartDateSelector,
  (by_start) => (`${by_start.next_seven.length} tasks start in the next 7 days`)
)


  
let lookahead_colors = {
  [Filter.in_progress] : chartColors.primary[0],
  [Filter.next_seven] : chartColors.primary[1],
  [Filter.next_fourteen] : chartColors.primary[2],
  [Filter.fourteen_plus]: chartColors.primary[3]
}

let _props = {
  title: PropTypes.string,
  data: PropTypes.shape({ 
    tasks: PropTypes.shape({
      startDate: PropTypes.shape({
        inProgress: _categoryShape,
        nextSevenDays: _categoryShape,
        nextFourteenDays: _categoryShape,
        fourteenPlusDays: _categoryShape
      })
    })
  }).isRequired,
  compact: PropTypes.bool.isRequired,
  onMore: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired  
}

// (dataset, current_date, current_filter) => [{}]
let lookaheadDataSelector = createSelector(
  acttaskByStartDateSelector,
  (dataset, current_date, current_filter) => (current_filter),
  (by_startdate, current_filter) => (
    [
      {x: 'In Progress', filter: Filter.in_progress, y: by_startdate.in_progress.length},
      {x: 'In Next 7 Days', filter: Filter.next_seven, y: by_startdate.next_seven.length},
      {x: 'Next 8-14 Days', filter: Filter.next_fourteen,y: by_startdate.next_fourteen.length},      
      {x: '14+ Days', filter: Filter.fourteen_plus, y: by_startdate.fourteen_plus.length}
    ].map((d) => ({
      ...d,
      fill: lookahead_colors[d.filter],
      stroke: chartSelection_theme.strokeColor,
      strokeWidth: (d.y !== 0 && d.filter === current_filter) ? chartSelection_theme.strokeWidth : 0
    }))
  )
)

export let fields = [
  {
    id: 'wbs',
    label: 'Wbs',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis',
      width: '10%'
    }
  },
  {
    id: 'name',
    label: 'Name',
    style: {
      width: '600px',
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis'
    }
  },
  {
    id: 'start',
    label: 'Start Date',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis',
      width: '40%'      
    },
    hidden: {xsDown: true}
  },
  {
    id: 'duration',
    label: 'Duration',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis'    ,
      width: '10%'  
    },
    hidden: {xsDown: true}
  },
]

export let _MainChart = ({dataset, currentFilter, onSetFilter, radius, width, height, use_status_date, ...rest}) => {
  
  let lookup_date = use_status_date ? statusDateSelector(dataset) : moment()

  return (
    <div style={{width, height}}>
      <VictoryChart padding={{ top: 0, left: 120, bottom: 50}}>
        <VictoryBar
          width={300} height={200}
          data={lookaheadDataSelector(dataset, lookup_date, currentFilter)}
          domainPadding={{x: [10, 15], y: 5}}
          horizontal={true}
          dataComponent={
              <ClickableBar onClick={(d)=>{if(onSetFilter)onSetFilter(d.filter)}}/>  
          }
        />
      </VictoryChart>
    </div>
)}

let __Homepage = ({classes, dataset, filter, onSetFilter, currentFilter, use_status_date, onSetUseStatusDate, ...rest}) => (
  <div className={classes.root} >
    <_MainChart 
      dataset={dataset} 
      onSetFilter={onSetFilter}
      currentFilter={currentFilter}
      width={chartSizes.width.large}
      height={chartSizes.height.large}
      className={classes.mainChart}
      use_status_date={use_status_date}
      {...rest}
    />
    <FormGroup>
      <FormControlLabel
        control={
          <Switch
            checked={use_status_date}
            onChange={(ev, checked)=>{
              onSetUseStatusDate(checked)}
            }
          />
        }
        label={use_status_date ? 'Using Status Date' : 'Using Current Date'}
      />
    </FormGroup>
  </div>
)

let styles = theme => ({
  root: theme.mixins.gutters({
    paddingTop: 0,
    paddingBottom: 16,
    overflowX: 'auto',
  }),
  mainChart: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  legend : {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit,
    marginLeft: 'auto',
    marginRight: 'auto'
  }
})

export let _Homepage = withStyles(styles)(__Homepage)




// WEBPACK FOOTER //
// ./src/components/LookAhead.js